/** Must stay aligned with ProcessDateExpressionRenderer.DateTokenRegex. */
const DATE_TOKEN = /^\{\{\s*(today|now)\s*(?:([+-])\s*(\d+)\s*(hours?|h|days?|d|months?|mo|m)?)?\s*\}\}$/i;
const PLACEHOLDER = /^\{\{.*\}\}$/;

export const DATE_PLACEHOLDER_EXAMPLES = '{{today}}, {{now}}, {{today-7d}}, {{today+2m}}, {{now-5h}}';

export type DatePlaceholderBase = 'today' | 'now';
export type DatePlaceholderSign = '+' | '-';
export type DatePlaceholderUnit = 'h' | 'd' | 'm';

export interface DatePlaceholder {
  base: DatePlaceholderBase;
  sign: DatePlaceholderSign;
  amount: number;
  unit: DatePlaceholderUnit;
}

export function isSubscriptionDateToken(value: string): boolean {
  return DATE_TOKEN.test(value.trim());
}

export function parseDatePlaceholder(value: string | null | undefined): DatePlaceholder | null {
  if (typeof value !== 'string') {
    return null;
  }
  const match = DATE_TOKEN.exec(value.trim());
  if (!match) {
    return null;
  }
  const amount = match[3] ? Number(match[3]) : 0;
  return {
    base: match[1].toLowerCase() === 'now' ? 'now' : 'today',
    sign: match[2] === '+' ? '+' : '-',
    amount: Number.isFinite(amount) ? amount : 0,
    unit: normalizeDatePlaceholderUnit(match[4]),
  };
}

export function formatDatePlaceholder(placeholder: DatePlaceholder): string {
  const amount = parseDatePlaceholderAmount(placeholder.amount);
  const sign: DatePlaceholderSign = placeholder.sign === '+' ? '+' : '-';
  const unit: DatePlaceholderUnit = placeholder.unit === 'h' || placeholder.unit === 'm'
    ? placeholder.unit
    : 'd';
  if (amount === 0 && sign === '-' && unit === 'd') {
    return `{{${placeholder.base}}}`;
  }
  return `{{${placeholder.base}${sign}${amount}${unit}}}`;
}

export function parseDatePlaceholderAmount(value: unknown): number {
  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount) || amount < 0) {
    return 0;
  }
  return Math.trunc(amount);
}

export function validateSubscriptionDatePlaceholders(value: unknown): string | null {
  if (typeof value === 'string') {
    const text = value.trim();
    if (PLACEHOLDER.test(text) && !DATE_TOKEN.test(text)) {
      return 'Unknown date placeholder';
    }
    return null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const error = validateSubscriptionDatePlaceholders(item);
      if (error) {
        return error;
      }
    }
    return null;
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value)) {
      const error = validateSubscriptionDatePlaceholders(item);
      if (error) {
        return error;
      }
    }
  }
  return null;
}

function normalizeDatePlaceholderUnit(raw: string | undefined): DatePlaceholderUnit {
  if (!raw) {
    return 'd';
  }
  const unit = raw.toLowerCase();
  if (unit.startsWith('h')) {
    return 'h';
  }
  if (unit === 'm' || unit.startsWith('mo')) {
    return 'm';
  }
  return 'd';
}
